import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    CircularProgress,
    Alert,
    TextField,
    MenuItem,
    Grid,
    Breadcrumbs,
    IconButton,
    Tooltip,
    Snackbar
} from '@mui/material';
import {
    Add as AddIcon,
    Refresh as RefreshIcon,
    Home as HomeIcon,
    CardGiftcard as CardGiftcardIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { guestPassAPI } from '../../services/api';
import { GuestPass, GuestPassStatus } from '../../types/guestPass';
import { formatDate } from '../../utils/dateUtils';
import { GuestPassDialog } from '../../components/guestPass/GuestPassDialog';
import { GuestPassCard } from '../../components/guestPass/GuestPassCard';
import { CreateGuestAccountDialog } from '../../components/guestPass/CreateGuestAccountDialog';

const GuestPassManagement: React.FC = () => {
    const [guestPasses, setGuestPasses] = useState<GuestPass[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [statusFilter, setStatusFilter] = useState<string>(GuestPassStatus.ACTIVE);
    const [searchFilter, setSearchFilter] = useState<string>('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [guestAccountDialogOpen, setGuestAccountDialogOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
    const [selectedPass, setSelectedPass] = useState<GuestPass | null>(null);
    const [actionLoading, setActionLoading] = useState<string | null>(null);

    useEffect(() => {
        loadGuestPasses();
    }, [statusFilter]);

    const loadGuestPasses = async () => {
        setLoading(true);
        setError(null);
        try {
            const filters: any = {};
            if (statusFilter !== 'all') {
                filters.status = statusFilter;
            }

            const response = await guestPassAPI.getAll(filters);
            setGuestPasses(response.data);
        } catch (err: any) {
            console.error('Error loading guest passes:', err);
            setError(err.response?.data?.message || 'Failed to load guest passes');
        } finally {
            setLoading(false);
        }
    };

    const handleGuestPassCreated = (newPass: GuestPass) => {
        setGuestPasses([newPass, ...guestPasses]);
        setSnackbarMessage('Guest pass created successfully!');
    };

    const handleGuestAccountCreated = () => {
        setSnackbarMessage('Guest account created successfully!');
    };

    const handleRedeemPass = async (passId: string, event: React.MouseEvent) => {
        event.stopPropagation();
        setActionLoading(passId);
        try {
            await guestPassAPI.redeem(passId);
            setSnackbarMessage('Guest pass redeemed successfully!');
            loadGuestPasses();
            if (selectedPass?.id === passId) {
                setSelectedPass(null);
            }
        } catch (err: any) {
            console.error('Error redeeming guest pass:', err);
            setSnackbarMessage(err.response?.data?.message || 'Failed to redeem guest pass');
        } finally {
            setActionLoading(null);
        }
    };

    const handleCancelPass = async (passId: string, event: React.MouseEvent) => {
        event.stopPropagation();
        setActionLoading(passId);
        try {
            await guestPassAPI.cancel(passId);
            setSnackbarMessage('Guest pass cancelled successfully!');
            loadGuestPasses();
            if (selectedPass?.id === passId) {
                setSelectedPass(null);
            }
        } catch (err: any) {
            console.error('Error cancelling guest pass:', err);
            setSnackbarMessage(err.response?.data?.message || 'Failed to cancel guest pass');
        } finally {
            setActionLoading(null);
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const getStatusColor = (status: GuestPassStatus) => {
        switch (status) {
            case GuestPassStatus.ACTIVE:
                return 'success';
            case GuestPassStatus.REDEEMED:
                return 'info';
            case GuestPassStatus.EXPIRED:
                return 'warning';
            case GuestPassStatus.CANCELLED:
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusLabel = (status: GuestPassStatus) => {
        return status.charAt(0).toUpperCase() + status.slice(1);
    };

    // Filter passes based on search input (name, email, or code)
    const filteredPasses = guestPasses.filter((pass) => {
        if (!searchFilter) return true;
        
        const searchLower = searchFilter.toLowerCase();
        return (
            pass.guestName.toLowerCase().includes(searchLower) ||
            pass.guestEmail.toLowerCase().includes(searchLower) ||
            pass.code.toLowerCase().includes(searchLower)
        );
    });

    const paginatedPasses = filteredPasses.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 3 }}>
                <RouterLink
                    className="user-link"
                    to="/dashboard"
                    style={{ display: 'flex', alignItems: 'center' }}
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Home
                </RouterLink>
                <Typography
                    sx={{ display: 'flex', alignItems: 'center' }}
                    color="text.primary"
                >
                    <CardGiftcardIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Guest Pass Management
                </Typography>
            </Breadcrumbs>

            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1">
                    Guest Pass Management
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Tooltip title="Refresh">
                        <IconButton onClick={loadGuestPasses} disabled={loading}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="outlined"
                        startIcon={<PersonAddIcon />}
                        onClick={() => setGuestAccountDialogOpen(true)}
                    >
                        Add Guest Account
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setDialogOpen(true)}
                    >
                        Generate Pass
                    </Button>
                </Box>
            </Box>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 5 }}>
                        <TextField
                            fullWidth
                            label="Search by Name, Email, or Code"
                            value={searchFilter}
                            onChange={(e) => {
                                setSearchFilter(e.target.value);
                                setPage(0); // Reset to first page when searching
                            }}
                            placeholder="Enter guest name, email, or pass code"
                            helperText={`Showing ${filteredPasses.length} of ${guestPasses.length} passes`}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                            select
                            fullWidth
                            label="Status Filter"
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setPage(0); // Reset to first page when filtering
                            }}
                        >
                            <MenuItem value="all">All Statuses</MenuItem>
                            <MenuItem value={GuestPassStatus.ACTIVE}>Active</MenuItem>
                            <MenuItem value={GuestPassStatus.REDEEMED}>Redeemed</MenuItem>
                            <MenuItem value={GuestPassStatus.EXPIRED}>Expired</MenuItem>
                            <MenuItem value={GuestPassStatus.CANCELLED}>Cancelled</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }} sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button
                            variant="outlined"
                            fullWidth
                            onClick={() => {
                                setStatusFilter(GuestPassStatus.ACTIVE);
                                setSearchFilter('');
                                setPage(0);
                            }}
                        >
                            Clear Filters
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Loading State */}
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* Table */}
            {!loading && (
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Code</TableCell>
                                    <TableCell>Guest Name</TableCell>
                                    <TableCell>Guest Email</TableCell>
                                    <TableCell>Sponsor</TableCell>
                                    <TableCell>Issued</TableCell>
                                    <TableCell>Valid Until</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedPasses.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                                                No guest passes found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedPasses.map((pass) => (
                                        <TableRow 
                                            key={pass.id}
                                            hover
                                            sx={{ cursor: 'pointer' }}
                                            onClick={() => setSelectedPass(pass)}
                                        >
                                            <TableCell>
                                                <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
                                                    {pass.code}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{pass.guestName}</TableCell>
                                            <TableCell>{pass.guestEmail}</TableCell>
                                            <TableCell>
                                                {pass.memberProfile 
                                                    ? `${pass.memberProfile.firstName} ${pass.memberProfile.lastName}`
                                                    : 'N/A'
                                                }
                                            </TableCell>
                                            <TableCell>{formatDate(pass.issuedAt)}</TableCell>
                                            <TableCell>{formatDate(pass.validUntil)}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={getStatusLabel(pass.status)}
                                                    color={getStatusColor(pass.status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                    {pass.status === GuestPassStatus.ACTIVE && (
                                                        <>
                                                            <Tooltip title="Redeem Pass">
                                                                <IconButton
                                                                    size="small"
                                                                    color="success"
                                                                    onClick={(e) => handleRedeemPass(pass.id, e)}
                                                                    disabled={actionLoading === pass.id}
                                                                >
                                                                    {actionLoading === pass.id ? (
                                                                        <CircularProgress size={20} />
                                                                    ) : (
                                                                        <CheckCircleIcon />
                                                                    )}
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Cancel Pass">
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={(e) => handleCancelPass(pass.id, e)}
                                                                    disabled={actionLoading === pass.id}
                                                                >
                                                                    <CancelIcon />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={filteredPasses.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            )}

            {/* Selected Pass Detail Dialog */}
            {selectedPass && (
                <Paper sx={{ mt: 3, p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6">Guest Pass Details</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {selectedPass.status === GuestPassStatus.ACTIVE && (
                                <>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        startIcon={<CheckCircleIcon />}
                                        onClick={(e) => handleRedeemPass(selectedPass.id, e)}
                                        disabled={actionLoading === selectedPass.id}
                                    >
                                        Redeem Pass
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        startIcon={<CancelIcon />}
                                        onClick={(e) => handleCancelPass(selectedPass.id, e)}
                                        disabled={actionLoading === selectedPass.id}
                                    >
                                        Cancel Pass
                                    </Button>
                                </>
                            )}
                            <Button onClick={() => setSelectedPass(null)}>Close</Button>
                        </Box>
                    </Box>
                    <GuestPassCard guestPass={selectedPass} onCopyCode={(code) => setSnackbarMessage(`Code ${code} copied!`)} />
                </Paper>
            )}

            {/* Guest Pass Dialog */}
            <GuestPassDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSuccess={handleGuestPassCreated}
                isAdminMode={true}
            />

            {/* Create Guest Account Dialog */}
            <CreateGuestAccountDialog
                open={guestAccountDialogOpen}
                onClose={() => setGuestAccountDialogOpen(false)}
                onSuccess={handleGuestAccountCreated}
            />

            {/* Snackbar */}
            <Snackbar
                open={!!snackbarMessage}
                autoHideDuration={3000}
                onClose={() => setSnackbarMessage(null)}
                message={snackbarMessage}
            />
        </Container>
    );
};

export default GuestPassManagement;
